import type { PickedFile } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { useId, useLayoutEffect, useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent, type SyntheticEvent } from "react";
import { Icon } from "./Icon.tsx";
import { pickedFiles } from "./media.tsx";
import { cx } from "./tokens.ts";

export type TextFieldProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Visible label above the field. */
  label?: string;
  /**
   * The field's name when there is no visible `label` (a search bar, the composer): a visually hidden `<label>`.
   * Without either, the placeholder is used as the name (deprecated: give one of them).
   */
  accessibilityLabel?: string;
  /** A hint under the field (caption, text.secondary). `error` replaces it while set. */
  helperText?: string;
  /** Validation message under the field (danger), the field marked invalid; absent = valid. */
  error?: string;
  /** Not editable or focusable; dimmed. */
  disabled?: boolean;
  /** Focusable and selectable but not editable (no chrome change except a quieter background). */
  readOnly?: boolean;
  multiline?: boolean;
  /** Multiline: the height it starts at, in lines (default 1); it still grows with the text. */
  minLines?: number;
  /** Monospaced input (code, formulas). */
  mono?: boolean;
  secure?: boolean;
  /**
   * What the field holds, for password managers and autofill: the sign-in address ("username"; `passkeyAutofill`
   * also offers passkeys from it — web `autocomplete="username webauthn"`, iOS `.username`, Android
   * `ContentType.Username`), the current password or a new one (`.password` / `.newPassword`).
   */
  contentType?: "username" | "currentPassword" | "newPassword" | "name";
  passkeyAutofill?: boolean;
  /** A small button at the end of the field (show/hide password). */
  trailingAction?: { icon: IconName; label: string; onPress: () => void; testID: string };
  autoFocus?: boolean;
  /** Enter (without Shift in multiline fields). */
  onSubmit?: () => void;
  leadingIcon?: IconName;
  /** `oneTimeCode`: digits keypad + OTP autofill (iOS `.oneTimeCode`, Android autofill OTP, web `one-time-code`). */
  keyboard?: "default" | "email" | "number" | "url" | "oneTimeCode";
  /** "outlined" (default) = bordered field; "plain" = no chrome, for composers and search bars. */
  variant?: "outlined" | "plain";
  /** Escape key (web; Android back / iOS dismiss on native). */
  onCancel?: () => void;
  /**
   * Files or images pasted into the field (screenshots, copied files); the text isn't changed. Native: iOS
   * `.onPasteCommand(of: [.image, .fileURL])`, Android `Modifier.contentReceiver`.
   */
  onPasteFiles?: (files: PickedFile[]) => void;
  /** Caret / selection moved (typing, clicks, arrow keys), in UTF-16 offsets. Menus that follow typing read it. */
  onSelectionChange?: (start: number, end: number) => void;
  /**
   * Keys a menu driven from this field may take (↑ ↓ Enter Tab Esc, hardware keyboards); return true when handled and
   * the field does nothing else (no caret move, submit, focus change or cancel).
   */
  onKey?: (key: TextFieldKey) => boolean;
  /** Backspace in the empty field (chip inputs remove their last chip). Native: the same on hardware/soft keyboards where available. */
  onBackspaceEmpty?: () => void;
  /** Focuses the field and selects [start, end] whenever `seq` changes (e.g. the caret after an inserted mention). */
  selection?: { start: number; end: number; seq: number };
  /**
   * A suggestion list driven by this field is open (combobox semantics: aria-expanded). While it is, Escape belongs
   * to the field (closing the list) and an enclosing Popover does not dismiss on it.
   */
  suggestionsOpen?: boolean;
  testID: string;
};

export type TextFieldKey = "up" | "down" | "enter" | "tab" | "escape";

const KEYS: Record<string, TextFieldKey> = { ArrowUp: "up", ArrowDown: "down", Enter: "enter", Tab: "tab", Escape: "escape" };

const inputType = { default: "text", email: "email", number: "number", url: "url", oneTimeCode: "text" } as const;

export function TextField(p: TextFieldProps) {
  const id = useId();
  const area = useRef<HTMLTextAreaElement>(null);
  const line = useRef<HTMLInputElement>(null);
  const appliedSeq = useRef(p.selection?.seq);

  // Auto-grow multiline fields up to the composer max height (CSS max-height); cheap, runs after each render.
  useLayoutEffect(() => {
    const el = area.current;
    if (el === null) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  });

  // Programmatic caret (after the value it belongs to is rendered); only on a new seq, never on mount.
  const sel = p.selection;
  useLayoutEffect(() => {
    if (sel === undefined || sel.seq === appliedSeq.current) return;
    appliedSeq.current = sel.seq;
    const el = p.multiline === true ? area.current : line.current;
    if (el === null) return;
    el.focus();
    try {
      el.setSelectionRange(sel.start, sel.end);
    } catch {
      // email/number inputs have no selection API
    }
  }, [sel, p.multiline]);

  const reportSelection = (e: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    p.onSelectionChange?.(el.selectionStart ?? el.value.length, el.selectionEnd ?? el.value.length);
  };
  const onInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    p.onChange(e.target.value);
    reportSelection(e);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Backspace" && e.currentTarget.value === "" && p.onBackspaceEmpty !== undefined) {
      p.onBackspaceEmpty();
      return;
    }
    const key = KEYS[e.key];
    if (key !== undefined && p.onKey !== undefined && !e.nativeEvent.isComposing && !e.shiftKey && !e.altKey && !e.metaKey && !e.ctrlKey && p.onKey(key)) {
      e.preventDefault();
      return;
    }
    if (e.key === "Escape" && p.onCancel !== undefined) {
      e.preventDefault();
      p.onCancel();
      return;
    }
    if (e.key !== "Enter" || p.onSubmit === undefined || e.nativeEvent.isComposing) return;
    if (p.multiline === true && (e.shiftKey || e.altKey)) return;
    e.preventDefault();
    p.onSubmit();
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (p.onPasteFiles === undefined) return;
    const files = pickedFiles(e.clipboardData.files);
    if (files.length === 0) return;
    e.preventDefault();
    p.onPasteFiles(files);
  };

  const hiddenLabel = p.label === undefined ? (p.accessibilityLabel ?? p.placeholder) : undefined;
  const note = p.error ?? p.helperText;
  const noteId = `${id}-note`;
  const common = {
    id,
    onPaste,
    value: p.value,
    placeholder: p.placeholder,
    autoFocus: p.autoFocus,
    disabled: p.disabled,
    readOnly: p.readOnly,
    "data-testid": p.testID,
    "aria-invalid": p.error === undefined ? undefined : true,
    "aria-describedby": note === undefined ? undefined : noteId,
    role: p.suggestionsOpen === undefined ? undefined : ("combobox" as const),
    "aria-autocomplete": p.suggestionsOpen === undefined ? undefined : ("list" as const),
    "aria-expanded": p.suggestionsOpen,
    onKeyDown,
    onSelect: p.onSelectionChange === undefined ? undefined : reportSelection,
    className: "b-field-input",
  };
  return (
    <div
      className={cx(
        "b-field",
        `b-field-${p.variant ?? "outlined"}`,
        p.multiline === true && "b-field-multiline",
        p.mono === true && "b-field-mono",
        p.disabled === true && "b-field-disabled",
        p.readOnly === true && "b-field-readonly",
        p.error !== undefined && "b-field-error",
      )}
    >
      {p.label === undefined ? null : (
        <label className="b-field-label" htmlFor={id}>
          {p.label}
        </label>
      )}
      {hiddenLabel === undefined ? null : (
        <label className="b-visually-hidden" htmlFor={id}>
          {hiddenLabel}
        </label>
      )}
      <div className="b-field-box">
        {p.leadingIcon === undefined ? null : <Icon name={p.leadingIcon} size="icon.sm" color="text.tertiary" />}
        {p.multiline === true ? (
          <textarea {...common} ref={area} rows={p.minLines ?? 1} onChange={onInput} />
        ) : (
          <input
            {...common}
            type={p.secure === true ? "password" : inputType[p.keyboard ?? "default"]}
            inputMode={p.keyboard === "oneTimeCode" ? "numeric" : undefined}
            autoComplete={autoCompleteOf(p)}
            ref={line}
            onChange={onInput}
          />
        )}
        {p.trailingAction === undefined ? null : (
          <button
            type="button"
            className="b-field-action"
            aria-label={p.trailingAction.label}
            title={p.trailingAction.label}
            onClick={p.trailingAction.onPress}
            disabled={p.disabled}
            data-testid={p.trailingAction.testID}
          >
            <Icon name={p.trailingAction.icon} size="icon.sm" color="text.secondary" />
          </button>
        )}
      </div>
      {note === undefined ? null : (
        <span id={noteId} className={p.error === undefined ? "b-field-note" : "b-field-note b-field-note-error"}>
          {note}
        </span>
      )}
    </div>
  );
}

function autoCompleteOf(p: Pick<TextFieldProps, "contentType" | "passkeyAutofill" | "keyboard" | "secure">): string {
  if (p.contentType === "username") return p.passkeyAutofill === true ? "username webauthn" : "username";
  if (p.contentType === "currentPassword") return "current-password";
  if (p.contentType === "newPassword") return "new-password";
  if (p.contentType === "name") return "name";
  return p.keyboard === "email" ? "email" : p.keyboard === "oneTimeCode" ? "one-time-code" : p.secure === true ? "current-password" : "off";
}
